/**
 * @file Проверка необходимости пароля и разблокировка по challenge/HMAC.
 *
 * Использует существующий протокол сервера. Ошибки доступа и сервера
 * не должны ошибочно выглядеть как неверный пароль.
 */
import { generateHmacBase64 } from '../../services/cipher.ts'
import { b64Encode } from '../../services/encoding.ts'
import { makeTunnelRequest } from '../../services/request.ts'

export type UnlockErrorCode =
    | 'empty'
    | 'rejected'
    | 'access'
    | 'rateLimited'
    | 'unavailable'

export class UnlockError extends Error {
    readonly code: UnlockErrorCode

    constructor(code: UnlockErrorCode) {
        super(code)
        this.name = 'UnlockError'
        this.code = code
    }
}

export function unlockErrorCode(error: unknown): UnlockErrorCode {
    return error instanceof UnlockError ? error.code : 'unavailable'
}

export interface UnlockTarget {
    folderPath: string
    boxID: string
    token: string
}

function checkResponse(response: Response) {
    if (response.ok) return

    if (response.status === 401 || response.status === 403)
        throw new UnlockError('access')

    if (response.status === 429) throw new UnlockError('rateLimited')

    throw new UnlockError('unavailable')
}

export async function passwordRequired(
    target: UnlockTarget,
    signal: AbortSignal,
): Promise<boolean> {
    const response = await makeTunnelRequest({
        path: '/auth/password/required',
        boxID: target.boxID,
        token: target.token,
        opts: {
            headers: { 'x-folder-path': b64Encode(target.folderPath) },
            signal: AbortSignal.any([signal, AbortSignal.timeout(30_000)]),
        },
    })
    checkResponse(response)
    if (response.status === 204) return false

    if (response.status === 200) return true

    throw new UnlockError('unavailable')
}

export async function unlockStorage(
    target: UnlockTarget,
    password: string,
    signal: AbortSignal,
): Promise<void> {
    if (password === '') throw new UnlockError('empty')

    const bounded = AbortSignal.any([signal, AbortSignal.timeout(30_000)])
    const challengeResponse = await makeTunnelRequest({
        path: '/auth/challenge',
        boxID: target.boxID,
        token: target.token,
        opts: { signal: bounded, cache: 'no-store' },
    })
    checkResponse(challengeResponse)
    const challenge: unknown = await challengeResponse.json()
    if (
        typeof challenge !== 'object' ||
        challenge === null ||
        !('session' in challenge) ||
        typeof challenge.session !== 'string' ||
        !challenge.session ||
        !('nonce' in challenge) ||
        typeof challenge.nonce !== 'string' ||
        !challenge.nonce
    )
        throw new UnlockError('unavailable')

    const response = await makeTunnelRequest({
        path: '/auth/challenge',
        boxID: target.boxID,
        token: target.token,
        opts: {
            method: 'POST',
            signal: bounded,
            headers: { 'x-folder-path': b64Encode(target.folderPath) },
            body: JSON.stringify({
                session: challenge.session,
                // Порядок nonce/password задан существующим challenge-протоколом сервера.
                response: await generateHmacBase64({
                    key: challenge.nonce,
                    message: password,
                }),
            }),
        },
    })

    // Протокол здесь не различает неверный пароль и отклонённую unlock-сессию.
    // Поэтому остальные сетевые/серверные ошибки не называем неверным паролем.
    if ([400, 401, 403].includes(response.status))
        throw new UnlockError('rejected')

    checkResponse(response)
}
